import { useEffect, useId, useState } from 'react';

import { Button, Icon } from '@ds/core';
import { Alert, Modal, Stepper } from '@ds/feedback';
import { FormField, SearchField, Select, Switch } from '@ds/forms';
import { useAviso } from '@/comuns/toast';
import { useAlteracaoPendente } from '@/comuns/useAlteracaoPendente';
import { dados } from '@/data/porta';
import type { ModeloDeDocumento } from '@/data/tipos';

export interface NovoDocumentoModalProps {
    aberto: boolean;
    /** Modelo já escolhido — quando vem do "Usar" da lista de favoritos. */
    modeloInicial?: ModeloDeDocumento | null;
    /** Paciente já escolhido — quando a emissão sai da ficha dele. */
    contatoInicial?: string;
    modelos: ModeloDeDocumento[];
    pacientes: { id: string; nome: string }[];
    procedimentos: { id: string; nome: string }[];
    profissionais: { id: string; nome: string }[];
    onClose: () => void;
    onEmitido: () => void;
}

const PASSOS = [{ label: 'Modelo' }, { label: 'Dados' }, { label: 'Assinatura' }];

const DESCRICAO = [
    'Escolha um modelo publicado.',
    'Vincule ao paciente e ao procedimento.',
    'Registre como a assinatura vai ser colhida.',
];

export function NovoDocumentoModal({
    aberto, modeloInicial, contatoInicial, modelos, pacientes, procedimentos, profissionais, onClose, onEmitido,
}: NovoDocumentoModalProps) {
    const aviso = useAviso();
    const idProcedimento = useId();
    const idProfissional = useId();
    const idCanal = useId();

    const [passo, setPasso] = useState(0);
    const [modeloId, setModeloId] = useState('');
    const [busca, setBusca] = useState('');
    const [contatoId, setContatoId] = useState('');
    const [procedimentoId, setProcedimentoId] = useState('');
    const [profissionalId, setProfissionalId] = useState('');
    const [canal, setCanal] = useState<'whatsapp' | 'email'>('whatsapp');
    const [lembrete, setLembrete] = useState(true);
    const [erro, setErro] = useState<string | null>(null);
    const [salvando, setSalvando] = useState(false);
    const formSujo = useAlteracaoPendente({ modeloId, contatoId, procedimentoId, profissionalId, canal, lembrete }, aberto);

    /* Só modelo publicado é oferecido: rascunho vira documento pela metade. */
    const publicados = modelos.filter((m) => m.publicado);
    const modelo = publicados.find((m) => m.id === modeloId);

    useEffect(() => {
        if (!aberto) return;
        setErro(null);
        setPasso(modeloInicial ? 1 : 0);
        setModeloId(modeloInicial?.id ?? '');
        setBusca('');
        setContatoId(contatoInicial ?? '');
        setProcedimentoId('');
        setProfissionalId('');
        setCanal('whatsapp');
        setLembrete(true);
    }, [aberto, modeloInicial, contatoInicial]);

    if (!aberto) return null;

    const semAssinatura = modelo?.assinantes === 'Não requer assinatura';
    const filtrados = pacientes.filter((p) => !busca || p.nome.toLowerCase().includes(busca.toLowerCase())).slice(0, 6);
    /* O paciente que já veio escolhido fica à vista, mesmo fora dos seis primeiros. */
    const escolhidoForaDaLista = contatoId && !filtrados.some((p) => p.id === contatoId) ? pacientes.find((p) => p.id === contatoId) : undefined;
    const candidatos = escolhidoForaDaLista ? [escolhidoForaDaLista, ...filtrados] : filtrados;

    const emitir = async (enviar: boolean) => {
        setSalvando(true);
        try {
            const documento = await dados().emitirDocumento({
                modeloId,
                contatoId,
                procedimentoId: procedimentoId || undefined,
                profissionalId: profissionalId || undefined,
                canal,
                lembreteAutomatico: lembrete,
                enviar,
            });
            /* Não há provedor de assinatura digital: nada é "enviado". O
               documento é emitido e espera a assinatura que alguém colhe e
               registra — é isso que o aviso tem de dizer. */
            aviso({
                title: !enviar ? 'Documento salvo como rascunho'
                    : semAssinatura ? 'Documento emitido' : 'Documento emitido, aguardando assinatura',
                description: `${documento.titulo} · ${documento.paciente}`,
            });
            onEmitido();
            onClose();
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível emitir.');
        } finally {
            setSalvando(false);
        }
    };

    const podeAvancar = passo === 0 ? !!modeloId : passo === 1 ? !!contatoId : true;

    return (
        <Modal
            open onClose={onClose} width={560} dirty={formSujo}
            title="Novo documento"
            description={DESCRICAO[passo]}
            footer={
                <>
                    {passo > 0 && <Button variant="ghost" onClick={() => setPasso((p) => p - 1)}>Voltar</Button>}
                    <span style={{ marginLeft: 'auto' }} />
                    <Button variant="secondary" onClick={onClose}>Cancelar</Button>
                    {passo === 2 && (
                        <Button variant="ghost" onClick={() => void emitir(false)} disabled={salvando}>Salvar rascunho</Button>
                    )}
                    <Button
                        disabled={!podeAvancar} loading={salvando && passo === 2}
                        onClick={() => (passo < 2 ? setPasso((p) => p + 1) : void emitir(true))}
                    >
                        {passo < 2 ? 'Continuar' : semAssinatura ? 'Emitir documento' : 'Emitir para assinatura'}
                    </Button>
                </>
            }
        >
            <Stepper current={passo} steps={PASSOS} style={{ marginBottom: 18 }} />

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {erro && <Alert tone="danger" dismissible={false}>{erro}</Alert>}

                {passo === 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {publicados.length === 0 && (
                            <Alert tone="warning" dismissible={false} title="Nenhum modelo publicado">
                                Publique um modelo na aba Modelos para poder emitir documentos.
                            </Alert>
                        )}
                        {publicados.map((m) => {
                            const escolhido = m.id === modeloId;
                            return (
                                <button
                                    key={m.id} type="button" onClick={() => setModeloId(m.id)}
                                    aria-pressed={escolhido}
                                    style={{
                                        display: 'flex', alignItems: 'center', gap: 11, padding: 12, cursor: 'pointer',
                                        textAlign: 'left', width: '100%',
                                        border: `1px solid ${escolhido ? 'var(--burgundy-700)' : 'var(--border)'}`,
                                        background: escolhido ? 'var(--surface-brand-soft)' : 'var(--surface)',
                                        borderRadius: 'var(--radius-md)',
                                    }}
                                >
                                    <Icon name="file-text" size={16} />
                                    <span style={{ flex: 1, minWidth: 0 }}>
                                        <span style={{ display: 'block', fontSize: 13.5, fontWeight: 600 }}>{m.nome}</span>
                                        <span style={{ display: 'block', fontSize: 12.5, color: 'var(--text-secondary)' }}>
                                            {m.categoria} · {m.assinantes}
                                        </span>
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                )}

                {passo === 1 && (
                    <>
                        <Alert tone="neutral" dismissible={false} title={modelo?.nome ?? 'Modelo'}>
                            Os {modelo?.campos ?? 0} campos dinâmicos são preenchidos com os dados escolhidos aqui — e o
                            texto resultante fica gravado no documento.
                        </Alert>

                        <SearchField value={busca} onChange={setBusca} width="100%" placeholder="Buscar paciente" />
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {candidatos.map((p) => {
                                const escolhido = p.id === contatoId;
                                return (
                                    <button
                                        key={p.id} type="button" onClick={() => setContatoId(p.id)} aria-pressed={escolhido}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', cursor: 'pointer',
                                            textAlign: 'left', width: '100%', fontSize: 13.5,
                                            border: `1px solid ${escolhido ? 'var(--burgundy-700)' : 'var(--border)'}`,
                                            background: escolhido ? 'var(--surface-brand-soft)' : 'var(--surface)',
                                            borderRadius: 'var(--radius-md)',
                                        }}
                                    >
                                        <Icon name="user" size={15} />
                                        {p.nome}
                                    </button>
                                );
                            })}
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                            <FormField label="Procedimento" htmlFor={idProcedimento}>
                                <Select
                                    id={idProcedimento} value={procedimentoId} onChange={setProcedimentoId}
                                    options={[{ value: '', label: 'Sem procedimento' }].concat(procedimentos.map((p) => ({ value: p.id, label: p.nome })))}
                                />
                            </FormField>
                            <FormField label="Profissional" htmlFor={idProfissional}>
                                <Select
                                    id={idProfissional} value={profissionalId} onChange={setProfissionalId}
                                    options={[{ value: '', label: 'Sem profissional' }].concat(profissionais.map((p) => ({ value: p.id, label: p.nome })))}
                                />
                            </FormField>
                        </div>
                    </>
                )}

                {passo === 2 && (
                    <>
                        {semAssinatura ? (
                            <Alert tone="neutral" dismissible={false} title="Este modelo não pede assinatura">
                                O documento nasce concluído e fica no prontuário do paciente. Fila de assinatura que
                                ninguém vai assinar é fila que ninguém limpa.
                            </Alert>
                        ) : (
                            <Alert tone="info" dismissible={false} title="A assinatura ainda é registrada à mão">
                                Nenhum provedor de assinatura digital está conectado a esta clínica. O documento entra
                                como <strong>aguardando assinatura</strong>, e quem colher a assinatura registra aqui —
                                dizer que foi enviado sem ter para onde enviar seria mentir para a recepção.
                            </Alert>
                        )}

                        <FormField label="Assinantes">
                            <span style={{ fontSize: 13.5 }}>{modelo?.assinantes}</span>
                        </FormField>

                        {!semAssinatura && (
                            <>
                                <FormField label="Canal combinado com o paciente" htmlFor={idCanal}>
                                    <Select
                                        id={idCanal} value={canal} onChange={(v) => setCanal(v as 'whatsapp' | 'email')}
                                        options={[{ value: 'whatsapp', label: 'WhatsApp' }, { value: 'email', label: 'E-mail' }]}
                                    />
                                </FormField>
                                <Switch
                                    checked={lembrete} onChange={setLembrete} label="Lembrar a cada 24h"
                                    description="Fica marcado no documento; o envio automático entra quando o canal estiver conectado"
                                />
                            </>
                        )}
                    </>
                )}
            </div>
        </Modal>
    );
}
